<template>
  <div class="farm-center-detail">
    <div v-if="loading" class="loading-state">
      <div class="spinner"></div>
      <p>{{ t('farmDetail.loading') }}</p>
    </div>

    <div v-else-if="farmCenter" class="detail-wrapper">
      <Teleport v-if="actionsTargetId && actionsReady && farmMode !== 'unknown'" :to="`#${actionsTargetId}`">
        <SeasonSelector
          v-model="selectedSeasonId"
          :seasons="seasons"
          @create-season="openSeasonForm(null)"
        />
        <AeButton iconOnly variant="secondary" @click="editFarm">
          <AeIcon name="edit" :size="16" />
          <span>{{ t('farmDetail.edit') }}</span>
        </AeButton>
        <AeButton
          v-if="canDeleteFarmCenter"
          iconOnly variant="danger"
          @click="showDeleteFarmCenterConfirm = true"
        >
          <AeIcon name="trash" :size="16" />
          <span>{{ t('farmDetail.deleteFarm') }}</span>
        </AeButton>
        <AeButtonDropdown
          variant="secondary" iconOnly
          :items="addActions"
          @select="handleAddAction"
        >
          <AeIcon name="plus" :size="16" />
          <span>{{ t('farmDetail.add') }}</span>
        </AeButtonDropdown>
      </Teleport>

      <div ref="contentRef" class="detail-content">

        <!-- farmMode unknown: scegli tipo azienda -->
        <div v-if="!loading && farmMode === 'unknown'" class="farm-type-choice">
          <h3>{{ t('farmDetail.chooseModeTitle', { name: farmCenter?.name }) }}</h3>
          <p class="hint">{{ t('farmDetail.chooseModeHint') }}</p>
          <div class="farm-type-options">
            <AeCard class="farm-type-card" hoverable @click="chooseFarmMode('fields')">
              <AeIcon name="Layers" :size="28" class="farm-type-card-icon" />
              <div class="farm-type-card-text">
                <strong>{{ t('farmDetail.modeFields') }}</strong>
                <span>{{ t('farmDetail.modeFieldsDesc') }}</span>
              </div>
            </AeCard>
            <AeCard class="farm-type-card" hoverable @click="chooseFarmMode('zoo')">
              <AeIcon name="PawPrint" :size="28" class="farm-type-card-icon" />
              <div class="farm-type-card-text">
                <strong>{{ t('farmDetail.modeZoo') }}</strong>
                <span>{{ t('farmDetail.modeZooDesc') }}</span>
              </div>
            </AeCard>
            <AeCard class="farm-type-card" hoverable @click="chooseFarmMode('mixed')">
              <AeIcon name="LayoutGrid" :size="28" class="farm-type-card-icon" />
              <div class="farm-type-card-text">
                <strong>{{ t('farmDetail.modeMixed') }}</strong>
                <span>{{ t('farmDetail.modeMixedDesc') }}</span>
              </div>
            </AeCard>
          </div>
        </div>

        <!-- farmMode zoo: mostra solo allevamento -->
        <div v-else-if="!loading && farmMode === 'zoo'" class="zoo-only-wrapper">
          <ZooContent :farm-center-id="farmCenterId" />
        </div>

        <!-- farmMode fields/mixed: layout a 3 pannelli -->
        <template v-else-if="!loading && (farmMode === 'fields' || farmMode === 'mixed')">
          <!-- No seasons: pulsante per aprire modal -->
          <div v-if="seasons.length === 0" class="no-season-state">
            <AeIcon name="calendar" :size="48" class="no-season-icon" />
            <h3>{{ t('farmDetail.noSeasonTitle') }}</h3>
            <p>{{ t('farmDetail.noSeasonHint') }}</p>
            <AeButton variant="primary" @click="openSeasonForm(null)">{{ t('farmDetail.createSeason') }}</AeButton>
          </div>

          <!-- Split layout: 3 colonne — mappa, appezzamenti, dettagli -->
          <AePanelSplit
            v-else
            :panels="[
              { label: t('farmDetail.panelMap'),   icon: 'Map' },
              { label: t('farmDetail.panelPlots'), icon: 'Layers' },
              { label: t('farmDetail.panelDetails'), icon: 'List' }
            ]"
            :height="availableHeight"
            :breakpoint="1024"
            :default-panel="isMobile ? 1 : 0"
            :ratios="['30%', '45%', '25%']"
          >
          <!-- Pannello 0: mappa -->
          <template #panel-0>
            <div class="map-panel">
              <AeMap
                v-if="farmCenter?.lat && (farmCenter?.lon || farmCenter?.lng)"
                :opt="{
                  zoom: 15,
                  height: mapHeight,
                  baseLayers: baseLayers,
                  layer_selector: true,
                  fullscreen: true
                }"
                :layers="mapLayersWithTraps"
                @feature-click="onMapFeatureClick"
              />

              <!-- Legenda -->
              <div v-if="legend.length > 0 || trapMapLayer" class="map-legend">
                <h4 class="legend-title" @click="legendCollapsed = !legendCollapsed">
                  {{ t('farmDetail.legendCrops') }}
                  <AeIcon :name="legendCollapsed ? 'ChevronUp' : 'ChevronDown'" :size="14" />
                </h4>
                <div v-if="!legendCollapsed" class="legend-items">
                  <div v-for="item in legend" :key="item.id" class="legend-item">
                    <span class="legend-color" :style="{ backgroundColor: item.color }"></span>
                    <span class="legend-label">{{ item.name }}</span>
                  </div>
                </div>

                <div v-if="!legendCollapsed && trapMapLayer" class="legend-items legend-items--separate">
                  <div class="legend-item">
                    <span class="legend-dot" style="background-color: #9333ea"></span>
                    <span class="legend-label">{{ t('farmDetail.traps') }}</span>
                  </div>
                </div>
              </div>
            </div>
          </template>

          <!-- Pannello 1: Appezzamenti / Campi -->
          <template #panel-1>
            <div class="panel-1-layout">

            <!-- Greggi -->
            <div class="info-section">
              <!-- Herd presenti -->
              <template v-if="herds.length > 0">
                <div class="section-header">
                  <h3 class="section-title">{{ t('farmDetail.herdsCount', { count: herds.length }) }}</h3>
                </div>
                <div class="data-grid">
                  <AeCard v-for="herd in herds" :key="herd.id_herd" class="data-item" hoverable>
                    <template #header>
                      <div class="item-header">
                        <router-link :to="{ name: 'HerdHome', params: { farmCenterId, id_herd: herd.id_herd } }"
                          class="herd-link">
                          <strong>{{ herd.name }}</strong>
                        </router-link>
                      </div>
                    </template>
                    <div class="data-item-content">
                      <span class="text-muted">{{ herd.specie }}</span>
                      <router-link :to="{ name: 'HerdHome', params: { farmCenterId, id_herd: herd.id_herd } }">
                        <AeButton variant="ghost" size="small">
                          <AeIcon name="arrow-right" :size="14" />
                          {{ t('farmDetail.goToHerd') }}
                        </AeButton>
                      </router-link>
                    </div>
                  </AeCard>
                </div>
                <hr/>
              </template>

              <!-- Nessun herd: chiedi all'utente se ha attività zootecnica -->
              <AeCard v-else-if="farmCenter?.has_zoo !== false" class="zoo-prompt">
                <template #header>
                  <h3 class="section-title">{{ t('farmDetail.zooPromptTitle') }}</h3>
                </template>
                <p>{{ t('farmDetail.zooPromptQuestion') }}</p>
                <div class="zoo-prompt-actions">
                  <AeButton variant="primary" @click="router.push({ name: 'ZooHome', params: { farmCenterId } })">
                    <AeIcon name="check" :size="14" />
                    {{ t('farmDetail.zooPromptYes') }}
                  </AeButton>
                  <AeButton variant="ghost" @click="dismissZooPrompt">
                    {{ t('farmDetail.zooPromptNo') }}
                  </AeButton>
                </div>
              </AeCard>
            </div>

            <div class="info-panel">
              <div class="mb-5">
                <div class="section-header mb-3">
                  <h3 class="section-title">{{ t('farmDetail.plotsCount', { count: filteredCards.length }) }}</h3>
                  <AeButton variant="primary" iconOnly  data-testid="btn-new-plot" @click="openPlotForm(null)">
                    +<span>{{ t('farmDetail.newPlot') }}</span>
                  </AeButton>
                </div>

                <!-- Search + view toggle -->
                <div v-if="fieldCards.length > 0" class="search-toolbar">
                  <AeInput
                    v-model="plotSearchQuery"
                    :placeholder="t('farmDetail.searchPlaceholder')"
                    class="search-input"
                  >
                    <template #prefix>
                      <AeIcon name="search" :size="16" />
                    </template>
                  </AeInput>
                  <div class="view-toggle">
                    <AeButton
                      :variant="viewMode === 'card' ? 'primary' : 'ghost'"
                      size="small"
                      @click="viewMode = 'card'"
                      :title="t('farmDetail.viewCard')"
                    >
                      <AeIcon name="grid" :size="16" />
                    </AeButton>
                    <AeButton
                      :variant="viewMode === 'table' ? 'primary' : 'ghost'"
                      size="small"
                      @click="viewMode = 'table'"
                      :title="t('farmDetail.viewTable')"
                    >
                      <AeIcon name="list" :size="16" />
                    </AeButton>
                  </div>
                </div>

                <div v-if="fieldCards.length === 0" class="empty-data">
                  <div class="empty-choice-grid">
                    <AeCard class="empty-choice-card" hoverable @click="openPlotForm(null)">
                      <div class="empty-choice-body">
                        <AeIcon name="plusCircle" :size="32" class="empty-choice-icon" />
                        <p class="empty-choice-title">{{ t('farmDetail.createManually') }}</p>
                        <p class="empty-choice-desc">{{ t('farmDetail.createManuallyDesc') }}</p>
                      </div>
                    </AeCard>
                    <AeCard class="empty-choice-card" hoverable @click="router.push({ name: 'AddPlotsView', params: { farmCenterId } })">
                      <div class="empty-choice-body">
                        <AeIcon name="upload" :size="32" class="empty-choice-icon" />
                        <p class="empty-choice-title">{{ t('farmDetail.importFromFile') }}</p>
                        <p class="empty-choice-desc">{{ t('farmDetail.importFromFileDesc') }}</p>
                      </div>
                    </AeCard>
                  </div>
                </div>
                <div v-else-if="filteredCards.length === 0" class="empty-data">
                  <AeCard>
                    <p>{{ t('farmDetail.noResultsFor', { query: plotSearchQuery }) }}</p>
                  </AeCard>
                </div>

                <!-- Card View -->
                <div v-else-if="viewMode === 'card'" class="data-grid">
                  <template v-for="card in filteredCards" :key="card.id">
                    <CardField
                      v-if="card.type === 'field'"
                      :plot="card.plot"
                      :field="card.field"
                      :clickable="true"
                      :class="{ 'plot-card--hovered': hoveredPlotId === card.id_plot }"
                      @mouseenter="onCardPlotEnter(card.id_plot)"
                      @mouseleave="onCardPlotLeave(card.id_plot)"
                      @click="clickCard(card.id_field, card.id_plot)"
                    >
                      <template #actions>
                        <AeButtonGroup size="small">
                          <AeButton variant="ghost" @click="editPlot(card.plot)" :title="t('farmDetail.edit')">
                            <AeIcon name="edit" :size="14" />
                          </AeButton>
                          <AeButton variant="ghost" @click="confirmDeletePlot(card.plot)" :title="t('farmDetail.delete')">
                            <AeIcon name="Trash2" :size="14" />
                          </AeButton>
                        </AeButtonGroup>
                      </template>
                    </CardField>
                    <CardField
                      v-else
                      :plot="card.plot"
                      :class="['field-card--empty', { 'plot-card--hovered': hoveredPlotId === card.id_plot }]"
                      @mouseenter="onCardPlotEnter(card.id_plot)"
                      @mouseleave="onCardPlotLeave(card.id_plot)"
                      @click="clickCard(card.id_field, card.id_plot)"
                    >
                      <template #actions>
                        <AeButtonGroup size="small">
                          <AeButton variant="ghost" @click.stop="openAddFieldForPlot(card.id_plot)" :title="t('farmDetail.addCrop')">
                            <AeIcon name="plus" :size="14" />
                          </AeButton>
                          <AeButton variant="ghost" @click="editPlot(card.plot)" :title="t('farmDetail.edit')">
                            <AeIcon name="edit" :size="14" />
                          </AeButton>
                          <AeButton variant="ghost" @click="confirmDeletePlot(card.plot)" :title="t('farmDetail.delete')">
                            <AeIcon name="Trash2" :size="14" />
                          </AeButton>
                        </AeButtonGroup>
                      </template>
                    </CardField>
                  </template>
                </div>

                <!-- Table View -->
                <div v-else class="data-table">
                  <AeTable :data="tableData" :opt="tableOpt">
                    <template #field__actions="{ row }">
                      <AeButtonGroup size="small">
                        <AeButton v-if="row.type === 'field'" variant="ghost" @click="goToField(row.id_field)" :title="t('farmDetail.open')">
                          <AeIcon name="eye" :size="14" />
                        </AeButton>
                        <AeButton variant="ghost" @click="editPlot(row.plot)" :title="t('farmDetail.edit')">
                          <AeIcon name="edit" :size="14" />
                        </AeButton>
                        <AeButton variant="ghost" @click="confirmDeletePlot(row.plot)" :title="t('farmDetail.delete')">
                          <AeIcon name="Trash2" :size="14" />
                        </AeButton>
                        <AeButton v-if="row.type === 'empty_plot'" variant="ghost" @click.stop="openAddFieldForPlot(row.id_plot)" :title="t('farmDetail.addCrop')">
                          <AeIcon name="plus" :size="14" />
                        </AeButton>
                      </AeButtonGroup>
                    </template>
                    <template #field_crop_name="{ row }">
                      <span v-if="row.type === 'field'" class="crop-dot-label">
                        <span class="crop-dot" :style="{ backgroundColor: row.crop_color }"></span>
                        {{ translateCropName(row.crop_name) }}
                      </span>
                      <span v-else class="crop-absent">{{ t('farmDetail.absent') }}</span>
                    </template>
                  </AeTable>
                </div>
              </div>
            </div>
            </div> <!-- end panel-1-layout -->
          </template>

          <!-- Pannello 2: Allevamenti + Elementi Geografici + Magazzino -->
          <template #panel-2>
            <div class="panel-layout">
            <div class="info-panel">

              

              

              <!-- Registro operazioni e osservazioni -->
              <div class="info-section">
                <div class="section-header">
                  <h3 class="section-title">{{ t('farmDetail.register') }}</h3>
                </div>
                <div class="data-grid">

                  <AeCard class="data-item summary-card" hoverable @click="router.push(`/poderi/${farmCenterId}/oo/operations`)">
                    <div class="summary-card-label">{{ t('farmDetail.operations') }}</div>
                    <div class="summary-card-count">{{ operations.length }}</div>
                    <AeIcon name="ChevronRight" :size="16" class="summary-card-arrow" />
                  </AeCard>

                  <AeCard class="data-item summary-card" hoverable @click="router.push(`/poderi/${farmCenterId}/oo/observations`)">
                    <div class="summary-card-label">{{ t('farmDetail.observations') }}</div>
                    <div class="summary-card-count">{{ observations.length }}</div>
                    <AeIcon name="ChevronRight" :size="16" class="summary-card-arrow" />
                  </AeCard>

                  <AeCard class="data-item summary-card" hoverable @click="router.push(`/poderi/${farmCenterId}/warehouse`)">
                    <div class="summary-card-label">{{ t('farmDetail.warehouse') }}</div>
                    <div class="summary-card-count">{{ poderiStore.products.length }}</div>
                    <AeIcon name="ChevronRight" :size="16" class="summary-card-arrow" />
                  </AeCard>
                </div>
              </div>

              <!-- Elementi Geografici -->
              <div class="info-section">
                <template v-if="geoElements.length === 0">
                  <AeButton variant="outline" small @click="openGeoElementForm(null)">{{ t('farmDetail.newGeoElement') }}</AeButton>
                </template>
                <template v-else>
                <div class="section-header">
                  <h3 class="section-title">{{ t('farmDetail.geoElementsCount', { count: geoElements.length }) }}</h3>
                  <AeButton variant="primary" small @click="openGeoElementForm(null)">{{ t('farmDetail.newGeoElementShort') }}</AeButton>
                </div>
                <div class="data-grid">
                  <AeCard v-for="element in geoElements" :key="element.id_geo_element" class="data-item" hoverable>
                    <template #header>
                      <div class="item-header">
                        <strong>{{ element.name }}</strong>
                        <div class="item-actions">
                          <AeButton variant="ghost" @click.stop="editGeoElement(element)" :title="t('farmDetail.edit')">
                            <AeIcon name="edit" :size="16" />
                          </AeButton>
                          <AeButton variant="ghost" @click.stop="confirmDeleteGeoElement(element)" :title="t('farmDetail.delete')">
                            <AeIcon name="Trash2" :size="16" />
                          </AeButton>
                        </div>
                      </div>
                    </template>
                    <div class="data-item-content">
                      <p><strong>{{ t('farmDetail.type') }}</strong> {{ getGeoElementTypeLabel(element.id_type, element) }}</p>
                      <p v-if="element.description" class="note">{{ element.description }}</p>
                      <router-link :to="`/poderi/${farmCenterId}/geo-element/${element.id_geo_element}`"
                        class="geo-element-detail-link" @click.stop>{{ t('farmDetail.surveys') }}</router-link>
                    </div>
                  </AeCard>
                </div>
                </template>
              </div>
            </div>
            </div> <!-- end panel-layout -->
          </template>
        </AePanelSplit>
            </template> <!-- end farmMode fields/mixed -->
          </div>
        </div>

    <div v-else class="error-state">
      <p>{{ t('farmDetail.farmNotFound') }}</p>
    </div>

    <FarmCenterFormModal
      v-model="showEditForm"
      :farm-center-id="farmCenterId"
      @submit="handleEditSubmit"
    />

    <!-- Plot Form Modal -->
    <AeModal v-model="showPlotForm" :title="t('farmDetail.plotModalTitle')" size="xlarge" flex-body>
      <PlotForm
        ref="plotFormRef"
        v-if="farmCenterId"
        :plot-id="editingPlotId"
        :farm-center-id="farmCenterId"
        :season-id="selectedSeasonId"
        entity-type="plot"
        :center-lat="farmCenter?.lat"
        :center-lon="farmCenter?.lon || farmCenter?.lng"
        :other-plots="plots.filter(p => p.id_plot !== editingPlotId)"
        @submit="handlePlotSubmit"
        @cancel="showPlotForm = false"
      />
      <template #footer>
        <div class="modal-actions">

          <AeButton
            v-if="plotFormRef?.stepperRef && !plotFormRef.stepperRef.isFirstStep"
            variant="secondary"
            @click="plotFormRef.stepperRef.prev()"
          >
            <AeIcon name="arrowLeft" :size="16" />
            <span>
              {{ t('farmDetail.back') }}
            </span>
          </AeButton>

          <AeButton
            v-if="plotFormRef?.stepperRef && !plotFormRef.stepperRef.isLastStep"
            variant="primary"  iconOnly
            data-testid="btn-plot-next"
            @click="plotFormRef.stepperRef.next()"
          >
            <AeIcon name="arrowRight" :size="16" />
            <span>{{ t('farmDetail.next') }}</span>
          </AeButton>

          <AeButton
            v-if="!plotFormRef?.stepperRef || plotFormRef.stepperRef.isLastStep"
            variant="primary"
            data-testid="btn-save-plot"
            @click="plotFormRef?.handleSubmit()"
            :loading="plotFormRef?.loading"
          >
            {{ plotFormRef?.loading ? t('farmDetail.saving') : (editingPlotId ? t('farmDetail.saveChanges') : t('farmDetail.createPlot')) }}
          </AeButton>
        </div>
      </template>
    </AeModal>

    <!-- GeoElement Form Modal -->
    <AeModal :flexBody="true" v-model="showGeoElementForm" :title="t('farmDetail.geoElementModalTitle')" size="xlarge">
      <PlotForm
        ref="geoElementFormRef"
        v-if="farmCenterId"
        :plot-id="editingGeoElementId"
        :farm-center-id="farmCenterId"
        entity-type="geo_element"
        :center-lat="farmCenter?.lat"
        :center-lon="farmCenter?.lon || farmCenter?.lng"
        :other-plots="allGeometries"
        @submit="handleGeoElementSubmit"
        @cancel="showGeoElementForm = false"
      />
      <template #footer>
        <div class="modal-actions">
          <AeButton variant="secondary" @click="geoElementFormRef?.handleCancel()">
            {{ t('farmDetail.cancel') }}
          </AeButton>
          <AeButton variant="primary" @click="geoElementFormRef?.handleSubmit()" :loading="geoElementFormRef?.loading">
            {{ geoElementFormRef?.loading ? t('farmDetail.saving') : (editingGeoElementId ? t('farmDetail.saveChanges') : t('farmDetail.createGeoElement')) }}
          </AeButton>
        </div>
      </template>
    </AeModal>

    <!-- Season Form Modal -->
    <AeModal v-model="showSeasonForm" :title="t('farmDetail.seasonModalTitle')" size="xlarge" flex-body>
      <SeasonForm
        v-if="farmCenterId"
        :season-id="editingSeasonId"
        :farm-center-id="farmCenterId"
        :previous-season-fields="currentSeasonFields"
        @submit="handleSeasonSubmit"
        @cancel="showSeasonForm = false"
      />
    </AeModal>

    <!-- Delete Plot Confirmation -->
    <AeModal v-model="showDeletePlotConfirm" :title="t('farmDetail.deletePlotTitle')" size="small">
      <p>{{ t('farmDetail.deletePlotConfirm', { name: itemToDelete?.name }) }}</p>
      <p v-if="plotFieldForDelete" class="helper-text">
        {{ t('farmDetail.deletePlotCascade', {
          season: plotFieldForDelete.year || plotFieldForDelete.season_name,
          crop: translateCropName(plotFieldForDelete.crop_name) || t('farmDetail.cropFallback')
        }) }}
      </p>
      <p class="helper-text">{{ t('farmDetail.cannotUndo') }}</p>

      <template #footer>
        <div class="modal-actions">
          <AeButton variant="secondary" @click="showDeletePlotConfirm = false">{{ t('farmDetail.cancel') }}</AeButton>
          <AeButton variant="danger" @click="deletePlot">{{ t('farmDetail.delete') }}</AeButton>
        </div>
      </template>
    </AeModal>

    <!-- Delete Season Confirmation -->
    <AeModal v-model="showDeleteSeasonConfirm" :title="t('farmDetail.confirmDeleteTitle')" size="small">
      <p>{{ t('farmDetail.deleteSeasonConfirm') }}</p>
      <p v-if="itemToDelete" class="confirmation-text">
        <strong>{{ itemToDelete.name || itemToDelete.season_name }}</strong>
      </p>
      <p class="helper-text">{{ t('farmDetail.cannotUndo') }}</p>

      <template #footer>
        <div class="modal-actions">
          <AeButton variant="secondary" @click="showDeleteSeasonConfirm = false">
            {{ t('farmDetail.cancel') }}
          </AeButton>
          <AeButton variant="danger" @click="deleteSeason">
            {{ t('farmDetail.delete') }}
          </AeButton>
        </div>
      </template>
    </AeModal>

    <!-- Delete GeoElement Confirmation -->
    <AeModal v-model="showDeleteGeoElementConfirm" :title="t('farmDetail.confirmDeleteTitle')" size="small">
      <p>{{ t('farmDetail.deleteGeoElementConfirm') }}</p>
      <p v-if="itemToDelete" class="confirmation-text">
        <strong>{{ itemToDelete.name }}</strong>
      </p>
      <p class="helper-text">{{ t('farmDetail.cannotUndo') }}</p>

      <template #footer>
        <div class="modal-actions">
          <AeButton variant="secondary" @click="showDeleteGeoElementConfirm = false">
            {{ t('farmDetail.cancel') }}
          </AeButton>
          <AeButton variant="danger" @click="deleteGeoElement">
            {{ t('farmDetail.delete') }}
          </AeButton>
        </div>
      </template>
    </AeModal>

    <!-- Operation Form Modal -->
    <OperationObservationModal
      v-model="showOperationForm"
      :title="t('farmDetail.operationModalTitle')"
      type="crop_operation"
      :id-farm-center="farmCenterId"
      :id-season="currentSeasonId"
      :edit-object="editingOperation"
      @saved="handleOperationSaved"
      @cancel="showOperationForm = false"
    />

    <!-- Observation Form Modal -->
    <OperationObservationModal
      v-model="showObservationForm"
      :title="t('farmDetail.observationModalTitle')"
      type="observation"
      :id-farm-center="farmCenterId"
      :id-season="currentSeasonId"
      :edit-object="editingObservation"
      @saved="handleObservationSaved"
      @cancel="showObservationForm = false"
    />

    <!-- Delete Operation Confirmation -->
    <AeModal v-model="showDeleteOperationConfirm" :title="t('farmDetail.confirmDeleteTitle')">
      <p>{{ t('farmDetail.deleteOperationConfirm') }}</p>
      <p class="helper-text">{{ t('farmDetail.cannotUndo') }}</p>

      <template #footer>
        <div class="modal-actions">
          <AeButton variant="secondary" @click="showDeleteOperationConfirm = false">
            {{ t('farmDetail.cancel') }}
          </AeButton>
          <AeButton variant="danger" @click="deleteOperation">
            {{ t('farmDetail.delete') }}
          </AeButton>
        </div>
      </template>
    </AeModal>

    <!-- Delete Observation Confirmation -->
    <AeModal v-model="showDeleteObservationConfirm" :title="t('farmDetail.confirmDeleteTitle')">
      <p>{{ t('farmDetail.deleteObservationConfirm') }}</p>
      <p class="helper-text">{{ t('farmDetail.cannotUndo') }}</p>

      <template #footer>
        <div class="modal-actions">
          <AeButton variant="secondary" @click="showDeleteObservationConfirm = false">
            {{ t('farmDetail.cancel') }}
          </AeButton>
          <AeButton variant="danger" @click="deleteObservation">
            {{ t('farmDetail.delete') }}
          </AeButton>
        </div>
      </template>
    </AeModal>

    <!-- Add Field (Crop Selector) Modal -->
    <AeModal v-model="showAddFieldModal" :title="t('farmDetail.addCropModalTitle')" size="medium">
      <div class="crop-selector-content">
        <p class="crop-selector-plot">
          {{ t('farmDetail.plotLabel') }} <strong>{{ addFieldPlotName }}</strong>
        </p>
        <AeSelect
          v-model="addFieldCropId"
          :options="cropOptionsForAdd"
          :label="t('farmDetail.cropLabel')"
          :placeholder="t('farmDetail.selectCropPlaceholder')"
          searchable
        />
      </div>
      <template #footer>
        <div class="modal-actions">
          <AeButton variant="secondary" @click="showAddFieldModal = false">{{ t('farmDetail.cancel') }}</AeButton>
          <AeButton variant="primary" @click="confirmAddField" :disabled="!addFieldCropId">{{ t('farmDetail.add') }}</AeButton>
        </div>
      </template>
    </AeModal>

    <!-- Product Form Modal -->
    <AeModal v-model="showProductForm" :title="t('farmDetail.productModalTitle')" size="large" :flexBody="true">
      <ProductForm
        v-if="farmCenterId && productFormType"
        :product-id="editingProductId"
        :farm-center-id="farmCenterId"
        :product-type="productFormType"
        @submit="handleProductSubmit"
        @cancel="showProductForm = false"
      />
    </AeModal>

    <!-- Delete Farm Center Confirmation -->
    <AeModal v-model="showDeleteFarmCenterConfirm" :title="t('farmDetail.deleteFarm')" size="small">
      <p>{{ t('farmDetail.deleteFarmConfirm') }}</p>
      <p class="confirmation-text"><strong>{{ farmCenter?.name }}</strong></p>
      <p class="helper-text">{{ t('farmDetail.cannotUndo') }}</p>
      <template #footer>
        <div class="modal-actions">
          <AeButton variant="secondary" @click="showDeleteFarmCenterConfirm = false">{{ t('farmDetail.cancel') }}</AeButton>
          <AeButton variant="danger" :loading="deletingFarmCenter" @click="doDeleteFarmCenter">{{ t('farmDetail.delete') }}</AeButton>
        </div>
      </template>
    </AeModal>

    <!-- Delete Product Confirmation -->
    <AeModal v-model="showDeleteProductConfirm" :title="t('farmDetail.confirmDeleteTitle')" size="small">
      <p>{{ t('farmDetail.deleteProductConfirm') }}</p>
      <p v-if="productToDelete" class="confirmation-text">
        <strong>{{ productToDelete.name }}</strong>
      </p>
      <p class="helper-text">{{ t('farmDetail.cannotUndo') }}</p>

      <template #footer>
        <div class="modal-actions">
          <AeButton variant="secondary" @click="showDeleteProductConfirm = false">
            {{ t('farmDetail.cancel') }}
          </AeButton>
          <AeButton variant="danger" @click="deleteProduct">
            {{ t('farmDetail.delete') }}
          </AeButton>
        </div>
      </template>
    </AeModal>

    <!-- Dettaglio trappola (click su marker mappa) -->
    <AeModal v-model="showTrapDetail" :title="selectedTrap?.name || t('farmDetail.trapFallbackTitle')" size="large" :flexBody="true">
      <TrapCard
        v-if="selectedTrap"
        :trap="selectedTrap"
        :id-season="currentSeasonId"
        @edit-trap="openTrapEdit"
        @delete-trap="confirmDeleteTrap"
      />
    </AeModal>

    <!-- Modal upsert trappola -->
    <AeModal v-model="showTrapUpsert" :title="editingTrapId ? t('farmDetail.editTrap') : t('farmDetail.newTrap')" size="large" :flexBody="true">
      <TrapUpsert
        v-if="showTrapUpsert"
        ref="trapUpsertRef"
        :trap-id="editingTrapId"
        :id-farm-center="farmCenterId"
        :id-season="currentSeasonId"
        @submit="handleTrapUpserted"
        @cancel="showTrapUpsert = false"
      />
      <template #footer>
        <div class="modal-actions">
          <AeButton variant="secondary" @click="showTrapUpsert = false">{{ t('farmDetail.cancel') }}</AeButton>
          <AeButton variant="primary" :loading="trapUpsertRef?.saving" @click="trapUpsertRef?.save()">{{ t('farmDetail.save') }}</AeButton>
        </div>
      </template>
    </AeModal>

    <!-- Delete Trap Confirmation -->
    <AeModal v-model="showTrapDeleteConfirm" :title="t('farmDetail.deleteTrapTitle')" size="small">
      <p>{{ t('farmDetail.deleteTrapConfirm', { name: trapToDelete?.name }) }}</p>
      <p class="helper-text">{{ t('farmDetail.cannotUndo') }}</p>
      <template #footer>
        <div class="modal-actions">
          <AeButton variant="secondary" @click="showTrapDeleteConfirm = false">{{ t('farmDetail.cancel') }}</AeButton>
          <AeButton variant="danger" @click="doDeleteTrap">{{ t('farmDetail.delete') }}</AeButton>
        </div>
      </template>
    </AeModal>

    <!-- Parla con Poderi — floating action button -->
    <button
      v-if="appStore.hasFeatureFlag('parlaconpoderi')"
      class="voice-fab"
      :title="t('farmDetail.voiceFab')"
      @click="router.push({ name: 'ParlaConView', query: { farmCenterId } })"
    >
      <AeIcon name="Mic" :size="22" />
    </button>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, watch, inject } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { translateCropName } from '../i18n/cropVocab'
import { useAppStore } from '../stores/app.js'
import { usePoderiStore } from '../stores/poderiStore'
import { useDbStore } from '../stores/dbStore'
import { useZooStore } from '../modules/zoo/zooStore'
import { useAvailableHeight } from '../composables/useAvailableHeight'
import { useMapLayers } from '../composables/useMapLayers'
import { AeAlert, AeBadge, AeButton, AeButtonDropdown, AeButtonGroup, AeCard, AeMap, AeModal, AeIcon, AeInput, AeTable, AePanelSplit } from '@aevue/components'
import FarmCenterFormModal from './FarmCenterFormModal.vue'
import PlotForm from './PlotForm.vue'
import SeasonForm from './SeasonForm.vue'
import ZooContent from '../modules/zoo/components/ZooContent.vue'
import SeasonSelector from './SeasonSelector.vue'
import OperationObservationModal from './OperationObservationModal.vue'
import ProductForm from './ProductForm.vue'
import CardField from '@/components/CardField.vue'
import TrapCard from './TrapCard.vue'
import TrapUpsert from './TrapUpsert.vue'

const props = defineProps({
  farmCenterId: { type: String, required: true },
  breadcrumbs: { type: Array, default: () => [] }
})

const route = useRoute()
const router = useRouter()
const { t } = useI18n()
const appStore = useAppStore()
const poderiStore = usePoderiStore()
const dbStore = useDbStore()
const zooStore = useZooStore()

const legendCollapsed = ref(false)

const setHeader = inject('poderiLayoutSetHeader', null)
const clearHeader = inject('poderiLayoutClearHeader', null)
const actionsTargetId = inject('poderiLayoutActionsId', null)
const actionsReady = ref(false)

// Get farmCenterId from props
const farmCenterId = props.farmCenterId

// Selected season - usa la route o l'ultima disponibile
const selectedSeasonId = ref(null)

// Available height for layout
const contentRef = ref(null)
const { availableHeight } = useAvailableHeight(contentRef, { bottomOffset: 32, mobileHeight: null })

const MOBILE_BREAKPOINT = 1024
const windowWidth = ref(window.innerWidth)
const isMobile = computed(() => windowWidth.value <= MOBILE_BREAKPOINT)
const mapHeight = computed(() => isMobile.value ? '55dvh' : availableHeight.value + 'px')

function onResize() { windowWidth.value = window.innerWidth }
onMounted(() => window.addEventListener('resize', onResize))
onUnmounted(() => window.removeEventListener('resize', onResize))

const farmCenter = computed(() => poderiStore.currentFarmCenter)
const plots = ref([])
const crops = ref([])
const fields = ref([])
const plotsWithFields = ref([])
const geoElements = ref([])
const geoElementTypes = ref([])
const seasons = ref([])
const herds = ref([])
const loading = ref(false)

// farmMode: derived from farm_center flags + legacy data inference
const farmMode = computed(() => {
  if (loading.value) return null

  const fc = farmCenter.value
  const hasF = fc?.has_fields || plots.value.length > 0
  const hasZ = fc?.has_zoo   || herds.value.length  > 0
  if (hasF && hasZ) return 'mixed'
  if (hasF)         return 'fields'
  if (hasZ)         return 'zoo'
  return 'unknown'
})

async function chooseFarmMode(mode) {
  const has_fields = mode === 'fields' || mode === 'mixed'
  const has_zoo    = mode === 'zoo'    || mode === 'mixed'
  await dbStore.update('farm_center', { id_farm_center: farmCenterId }, { has_fields, has_zoo })
  // aggiorna localmente senza reload completo
  if (farmCenter.value) {
    farmCenter.value.has_fields = has_fields
    farmCenter.value.has_zoo    = has_zoo
  }
}

// Search & view mode
const plotSearchQuery = ref('')
const viewMode = ref('card') // 'card' | 'table'

// Build flat list of field cards (one card per field, plus empty-plot cards)
const fieldCards = computed(() => {
  const cards = []
  for (const plot of plotsWithFields.value) {
    if (plot.fields && plot.fields.length > 0) {
      for (const field of plot.fields) {
        cards.push({
          type: 'field',
          id: field.id_field,
          id_field: field.id_field,
          id_plot: plot.id_plot,
          plot_name: plot.name,
          plot_type: plot.plot_type,
          area: plot.area || plot.surface || 0,
          crop_name: translateCropName(field.crop?.name || field.crop?.crop_name) || 'N/D',
          crop_color: field.crop?.color || '#999',
          note: plot.note,
          plot: plot,
          field: field
        })
      }
    } else {
      cards.push({
        type: 'empty_plot',
        id: plot.id_plot,
        id_plot: plot.id_plot,
        plot_name: plot.name,
        plot_type: plot.plot_type,
        area: plot.area || plot.surface || 0,
        crop_name: null,
        crop_color: null,
        note: plot.note,
        plot: plot,
        field: null
      })
    }
  }
  return cards
})

// Filtered cards based on search query
const filteredCards = computed(() => {
  if (!plotSearchQuery.value) {
    return fieldCards.value
  }
  const query = plotSearchQuery.value.toLowerCase()
  return fieldCards.value.filter(card => {
    if (card.plot_name?.toLowerCase().includes(query)) return true
    if (card.crop_name?.toLowerCase().includes(query)) return true
    if (getPlotTypeLabel(card.plot_type)?.toLowerCase().includes(query)) return true
    return false
  })
})

// Table config for AeTable view
const tableOpt = {
  search: false,
  fields: [
    { field: '_actions', label: '', sortable: false },
    { field: 'plot_name', label: 'Appezzamento' },
    { field: 'crop_name', label: 'Coltura' },
    // { field: 'plot_type_label', label: 'Tipologia' },
    { field: 'area_display', label: 'Superficie (m²)', "type": 'numeric' },
  ]
}

const tableData = computed(() => {
  return filteredCards.value.map(card => ({
    ...card,
    plot_type_label: getPlotTypeLabel(card.plot_type),
    area_display: card.area.toFixed(0)
  }))
})

// Computed: field della season corrente per passarli al SeasonForm
const currentSeasonFields = computed(() => {
  const result = []
  for (const plot of plotsWithFields.value) {
    if (plot.fields && plot.fields.length > 0) {
      for (const field of plot.fields) {
        result.push({
          id_plot: plot.id_plot,
          plot_name: plot.name,
          id_crop: field.id_crop,
          crop_name: translateCropName(field.crop?.name || field.crop?.crop_name) || 'N/D'
        })
      }
    }
  }
  return result
})

// Crop options for add-field modal
const cropOptionsForAdd = computed(() => {
  return (crops.value || [])
    .map(crop => {
      const rawName = crop.name || crop.crop_name
      return { value: crop.id_crop, label: translateCropName(rawName), _default: crop.default ?? 0, _sortKey: rawName }
    })
    .sort((a, b) => b._default - a._default || a._sortKey.localeCompare(b._sortKey, 'it'))
    .map(({ _default, _sortKey, ...opt }) => opt)
})

// Elimina visibile solo all'owner (calcolato offline dalla tabella team locale)
const isOwner = ref(false)
poderiStore.isFarmCenterOwner(farmCenterId).then(v => { isOwner.value = v })

const canDeleteFarmCenter = computed(
  () => isOwner.value && plots.value.length === 0 && seasons.value.length <= 1
)

// Modal states
const showEditForm = ref(false)
const showDeleteFarmCenterConfirm = ref(false)
const deletingFarmCenter = ref(false)
const showPlotForm = ref(false)
const showGeoElementForm = ref(false)
const showSeasonForm = ref(false)
const showDeletePlotConfirm = ref(false)
const plotFieldForDelete = ref(null)   // field da cancellare in cascata con il plot (solo se 1 field senza dipendenze)
const showDeleteSeasonConfirm = ref(false)
const showDeleteGeoElementConfirm = ref(false)
const editingPlotId = ref(null)
const editingGeoElementId = ref(null)
const editingSeasonId = ref(null)
const itemToDelete = ref(null)

// Computed per combinare plot e geo_element per la mappa
const allGeometries = computed(() => {
  const plotGeoms = plots.value.map(p => ({
    ...p,
    id: p.id_plot,
    type: 'plot'
  }))
  const geoGeoms = geoElements.value.map(g => ({
    ...g,
    id: g.id_geo_element,
    type: 'geo_element'
  }))
  return [...plotGeoms, ...geoGeoms]
})

// Operations and Observations modal states
const showOperationForm = ref(false)
const showObservationForm = ref(false)
const showDeleteOperationConfirm = ref(false)
const showDeleteObservationConfirm = ref(false)
const editingOperation = ref(null)
const editingObservation = ref(null)
const operationToDelete = ref(null)
const observationToDelete = ref(null)

// Product modal state
const showProductForm = ref(false)
const showDeleteProductConfirm = ref(false)
const editingProductId = ref(null)
const productFormType = ref(null)
const productToDelete = ref(null)

// Form refs for modal footer pattern
const plotFormRef = ref(null)
const geoElementFormRef = ref(null)

// Add field modal state
const showAddFieldModal = ref(false)
const addFieldPlotId = ref(null)
const addFieldPlotName = ref('')
const addFieldCropId = ref(null)

// Set degli id_field appartenenti alla stagione corrente (da plotsWithFields)
const seasonFieldIds = computed(() => {
  const ids = new Set()
  for (const plot of plotsWithFields.value) {
    for (const field of (plot.fields || [])) {
      ids.add(field.id_field)
    }
  }
  return ids
})

// Filtra operations e observations per stagione corrente tramite i field IDs
const operations = computed(() => {
  const ids = seasonFieldIds.value
  if (!ids.size) return []
  return poderiStore.operations.filter(op => {
    const fields = Array.isArray(op.id_fields) ? op.id_fields : op.id_field ? [op.id_field] : []
    return fields.some(id => ids.has(id))
  })
})
const observations = computed(() => {
  const ids = seasonFieldIds.value
  if (!ids.size) return []
  return poderiStore.observations.filter(obs => {
    const fields = Array.isArray(obs.id_fields) ? obs.id_fields : obs.id_field ? [obs.id_field] : []
    return fields.some(id => ids.has(id))
  })
})

// currentSeasonId segue selectedSeasonId (che è la stagione selezionata dall'utente)
const currentSeasonId = computed(() => selectedSeasonId.value)

// Use mapLayers composable
const hoveredPlotId = ref(null)
const { mapLayers, legend, legendGeoElements, baseLayers, plotLayerRefs } = useMapLayers({
    farmCenter,
    plotsWithFields,
    geoElements,
    geoElementTypes,
    getPlotTypeLabel,
    hoveredPlotId
})

// ── Trappole sulla mappa ───────────────────────────────────────────────────────
const traps              = ref([])
const showTrapDetail     = ref(false)
const selectedTrap       = ref(null)
const showTrapUpsert     = ref(false)
const editingTrapId      = ref(null)
const trapUpsertRef      = ref(null)
const showTrapDeleteConfirm = ref(false)
const trapToDelete       = ref(null)

async function loadTraps() {
  const res = await dbStore.select('trap', { id_farm_center: farmCenterId })
  traps.value = (res?.data || []).filter(t => !t.archived)
}

// Layer GeoJSON con le trappole georeferenziate, aggiunto a quelli del composable useMapLayers
const trapMapLayer = computed(() => {
  const features = traps.value
    .filter(t => t.lat && t.lon)
    .map(t => ({
      type: 'Feature',
      geometry: { type: 'Point', coordinates: [t.lon, t.lat] },
      properties: { id_trap: t.id_trap, name: t.name },
    }))
  if (!features.length) return null
  return {
    name: 'Trappole',
    type: 'geojson',
    data: { type: 'FeatureCollection', features },
    options: {
      point_to_circle: true,
      circleOptions: { radius: 8, fillOpacity: 0.9, color: '#fff', weight: 2, fillColor: '#9333ea' },
      popup: true,
      popupTemplate: (p) => `<strong>🪤 ${p.name}</strong>`,
    },
  }
})

const mapLayersWithTraps = computed(() => [
  ...mapLayers.value,
  ...(trapMapLayer.value ? [trapMapLayer.value] : []),
])

function onMapFeatureClick(feature) {
  // Gestisci clic su trappola
  const idTrap = feature?.properties?.id_trap
  if (idTrap) {
    const trap = traps.value.find(t => t.id_trap === idTrap)
    if (trap) {
      selectedTrap.value = trap
      showTrapDetail.value = true
    }
    return
  }


  // Gestisci clic su elemento geografico
  const idGeoElement = feature?.properties?.id_geo_element
  if (idGeoElement) {
    const geoElement = geoElements.value.find(g => g.id_geo_element === idGeoElement)
    if (geoElement) {
      // Naviga al dettaglio dell'elemento geografico
      router.push(`/poderi/${farmCenterId}/geo-element/${idGeoElement}`)
    }
  }
}

function openTrapEdit(trap) {
  showTrapDetail.value = false
  editingTrapId.value = trap.id_trap
  showTrapUpsert.value = true
}

async function handleTrapUpserted() {
  showTrapUpsert.value = false
  await loadTraps()
}

function confirmDeleteTrap(trap) {
  showTrapDetail.value = false
  trapToDelete.value = trap
  showTrapDeleteConfirm.value = true
}

async function doDeleteTrap() {
  if (!trapToDelete.value) return
  await dbStore.delete('trap', trapToDelete.value.id_trap)
  showTrapDeleteConfirm.value = false
  trapToDelete.value = null
  await loadTraps()
}

function onCardPlotEnter(plotId) {
  hoveredPlotId.value = plotId
  const layer = plotLayerRefs.get(plotId)
  if (layer) layer.setStyle({ fillOpacity: 0.85, weight: 4 })
}
function onCardPlotLeave(plotId) {
  hoveredPlotId.value = null
  const layer = plotLayerRefs.get(plotId)
  if (layer) layer.setStyle({ fillOpacity: 0.5, weight: 2 })
}

// Watch selectedSeasonId per navigare alla route con seasonId
watch(selectedSeasonId, async (newSeasonId, oldSeasonId) => {
    if (newSeasonId && newSeasonId !== oldSeasonId) {
        // Condividi la stagione selezionata con lo store (per persistenza tra views)
        poderiStore.currentSeasonId = newSeasonId

        // Naviga alla route con il nuovo seasonId
        if (route.params.seasonId !== String(newSeasonId)) {
            router.push({
                name: 'FarmCenterDetailWithSeason',
                params: { id: farmCenterId, seasonId: newSeasonId }
            })
        }

        // Ricarica i dati dei plot con i fields per la nuova stagione
        plotsWithFields.value = await poderiStore.getPlotFields(farmCenterId, newSeasonId)
    }
})

// Carica geo_element types
async function loadGeoElementTypes() {
  try {
    console.log('Loading geo_element_type table...')
    const result = await dbStore.select('geo_element_type')
    if (result.ok) {
      geoElementTypes.value = result.data || []
      console.log('Loaded geo element types:', geoElementTypes.value)
    } else {
      console.error('Failed to load geo_element_type:', result)
    }
  } catch (error) {
    console.error('Error loading geo element types:', error)
    console.error('Error details:', error.message, error.name)
  }
}

// Carica geo_elements
async function loadGeoElements() {
  try {
    const result = await dbStore.select('geo_element', {
      id_farm_center: farmCenterId
    })
    if (result.ok) {
      geoElements.value = result.data || []
      console.log('Loaded geo elements:', geoElements.value)
    }
  } catch (error) {
    console.error('Error loading geo elements:', error)
  }
}

// Ottiene label del tipo di elemento geografico
function getGeoElementTypeLabel(idType, el) {


  if (!idType) return t('farmDetail.notAvailable')

  // Converti entrambi a stringa per il confronto (potrebbero essere number o string)
  const type = geoElementTypes.value.find(gt =>
    String(gt.id_geo_element_type) === String(idType)
  )

  return type?.name || type?.label || type?.code || t('farmDetail.typeFallback', { id: idType })
}

async function loadData() {
    console.log('Loading farm center with ID:', farmCenterId)

    if (!farmCenterId) {
        console.error('Farm center ID is missing!')
        return
    }

    await poderiStore.fetchFarmCenterById(farmCenterId)
    if (farmCenter.value) {
        const [plotsData, seasonsData, cropsData, fieldsData] = await Promise.all([
            poderiStore.fetchPlotsByFarmCenter(farmCenterId),
            poderiStore.fetchSeasonsByFarmCenter(farmCenterId),
            poderiStore.fetchCrops(),
            poderiStore.fetchFieldsByFarmCenter(farmCenterId),
            loadGeoElementTypes(),
            loadGeoElements(),
            poderiStore.fetchProductsByFarmCenter(farmCenterId),
            loadTraps()
        ])
        plots.value = plotsData || []
        seasons.value = seasonsData || []
        crops.value = cropsData || []
        fields.value = fieldsData || []

        // Inizializza selectedSeasonId dalla route o usa l'ultima stagione
        if (seasons.value.length > 0) {
            const routeSeasonId = route.params.seasonId
            // Verifica che la stagione dalla route appartenga a QUESTO farm center
            const seasonBelongsToFarm = routeSeasonId && seasons.value.some(
                s => String(s.id_season) === String(routeSeasonId)
            )

            if (seasonBelongsToFarm) {
                selectedSeasonId.value = routeSeasonId
            } else {
                // Seleziona l'ultima stagione (per data) di questo farm center
                const sortedSeasons = [...seasons.value].sort((a, b) => {
                    const dateA = new Date(a.date_from || '0000-01-01')
                    const dateB = new Date(b.date_from || '0000-01-01')
                    return dateB - dateA
                })
                const latestSeasonId = sortedSeasons[0]?.id_season
                if (latestSeasonId) {
                    router.replace({
                        name: 'FarmCenterDetailWithSeason',
                        params: { id: farmCenterId, seasonId: latestSeasonId }
                    })
                    selectedSeasonId.value = latestSeasonId
                }
            }
        } else {
            selectedSeasonId.value = null
        }
        
        // Load plots with fields for selected season
        if (selectedSeasonId.value) {
            plotsWithFields.value = await poderiStore.getPlotFields(farmCenterId, selectedSeasonId.value)
        }

        herds.value = await zooStore.getHerds(farmCenterId)
    }
}

async function createCurrentSeason() {
    const currentYear = new Date().getFullYear()
    const seasonName = `${currentYear}`

    const result = await dbStore.insert('season', {
        id_farm_center: farmCenterId,
        name: seasonName,
        year: currentYear,
        date_from: `${currentYear}-01-01`,
        date_to: `${currentYear}-12-31`,
        active: 1
    })

    if (result.ok) {
        await loadData()
        // The newly created season will be auto-selected by loadData
    } else {
        alert(t('farmDetail.errorCreatingSeason', { msg: result.message }))
    }
}

async function handleCropRotationRefresh() {
    // Reload all data related to crop rotation (plots, seasons, fields)
    const [plotsData, seasonsData, fieldsData] = await Promise.all([
        poderiStore.fetchPlotsByFarmCenter(farmCenterId),
        poderiStore.fetchSeasonsByFarmCenter(farmCenterId),
        poderiStore.fetchFieldsByFarmCenter(farmCenterId)
    ])
    plots.value = plotsData || []
    seasons.value = seasonsData || []
    fields.value = fieldsData || []
}

async function handleGanttRefresh() {
    // Reload all data related to Gantt planner (operations, observations, fields)
    const [fieldsData] = await Promise.all([
        poderiStore.fetchFieldsByFarmCenter(farmCenterId),
        poderiStore.fetchOperationsAndObservations(farmCenterId)
    ])
    fields.value = fieldsData || []
}

// Helper functions
function getPlotTypeLabel(type) {
    const types = {
        field: t('farmDetail.plotTypeField'),
        orchard: t('farmDetail.plotTypeOrchard'),
    }
    return types[type] || type
}

function formatDate(dateStr) {
    if (!dateStr) return ''
    const date = new Date(dateStr)
    return date.toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' })
}

async function dismissZooPrompt() {
    await dbStore.update('farm_center', farmCenterId, { has_zoo: false })
    await poderiStore.fetchFarmCenterById(farmCenterId)
}

// Navigation
function editFarm() {
    showEditForm.value = true
}

async function handleEditSubmit() {
    showEditForm.value = false
    await loadData()
}

// Plot actions
function openPlotForm(plotId) {
    editingPlotId.value = plotId
    showPlotForm.value = true
}

function openGeoElementForm(geoElementId = null) {
    editingGeoElementId.value = geoElementId
    showGeoElementForm.value = true
}

function editPlot(plot) {
    openPlotForm(plot.id_plot)
}

function editGeoElement(geoElement) {
    openGeoElementForm(geoElement.id_geo_element)
}


function clickCard(id_field, id_plot) {

  console.log('Card clicked - id_field:', id_field, 'id_plot:', id_plot)
    if (id_field) {
        goToField(id_field)
    } else if (id_plot) {
        openAddFieldForPlot(id_plot )
    }
}

function goToField(fieldId) {
    router.push({
        name: 'FieldDetailView',
        params: { farmCenterId: farmCenterId, fieldId: fieldId }
    })
}

// Add field to a plot (crop selector)
function openAddFieldForPlot(plotId) {
    const plot = plots.value.find(p => p.id_plot === plotId)
    addFieldPlotId.value = plotId
    addFieldPlotName.value = plot?.name || ''
    addFieldCropId.value = null
    showAddFieldModal.value = true
}

async function confirmAddField() {
    if (!addFieldPlotId.value || !addFieldCropId.value || !selectedSeasonId.value) return

    const result = await poderiStore.createField({
        id_farm_center: farmCenterId,
        id_plot: addFieldPlotId.value,
        id_season: selectedSeasonId.value,
        id_crop: addFieldCropId.value
    })

    if (result.ok) {
        showAddFieldModal.value = false
        await loadData()
    } else {
        alert(t('farmDetail.errorCreateField', { msg: result.message || '' }))
    }
}

async function confirmDeletePlot(plot) {
    plotFieldForDelete.value = null

    const fields = (plot.fields || []).filter(f => !f._deleted)

    if (fields.length > 1) {
        alert(t('farmDetail.errorCannotDeletePlot', { name: plot.name, count: fields.length }))
        return
    }

    if (fields.length === 1) {
        const field = fields[0]
        const [opsResult, obsResult] = await Promise.all([
            dbStore.select('operation', { id_field: field.id_field }),
            dbStore.select('observation', { id_field: field.id_field })
        ])
        const opsCount = opsResult?.ok ? (opsResult.data || []).length : 0
        const obsCount = obsResult?.ok ? (obsResult.data || []).length : 0
        if (opsCount > 0 || obsCount > 0) {
            const parts = []
            if (opsCount > 0) parts.push(t(opsCount > 1 ? 'farmDetail.operationsCount' : 'farmDetail.operationCount', { count: opsCount }))
            if (obsCount > 0) parts.push(t(obsCount > 1 ? 'farmDetail.observationsCount' : 'farmDetail.observationCount', { count: obsCount }))
            alert(t('farmDetail.errorCannotDeletePlotDeps', { season: field.year || field.season_name || '', parts: parts.join(t('farmDetail.andJoiner')) }))
            return
        }
        plotFieldForDelete.value = field
    }

    itemToDelete.value = plot
    showDeletePlotConfirm.value = true
}

function confirmDeleteGeoElement(geoElement) {
    itemToDelete.value = geoElement
    showDeleteGeoElementConfirm.value = true
}

async function deletePlot() {
    if (!itemToDelete.value) return
    try {
        // Se c'è un field da cancellare in cascata, lo elimina prima
        if (plotFieldForDelete.value) {
            const fieldResult = await poderiStore.deleteField(plotFieldForDelete.value.id_field)
            if (!fieldResult.ok) {
                showDeletePlotConfirm.value = false
                alert(fieldResult.message)
                return
            }
        }
        // Cancella il plot direttamente (i field sono già stati rimossi)
        const result = await dbStore.delete('plot', itemToDelete.value.id_plot)
        if (result.ok) {
            showDeletePlotConfirm.value = false
            itemToDelete.value = null
            plotFieldForDelete.value = null
            await loadData()
        } else {
            showDeletePlotConfirm.value = false
            alert(result.message || t('farmDetail.errorDeleteGeneric'))
        }
    } catch (error) {
        console.error('Error deleting plot:', error)
        alert(t('farmDetail.errorDeletePlot'))
    }
}

async function handlePlotSubmit() {
    showPlotForm.value = false
    editingPlotId.value = null
    await loadData()
}

async function handleGeoElementSubmit() {
    showGeoElementForm.value = false
    editingGeoElementId.value = null
    await loadGeoElements()
}

async function deleteGeoElement() {
    if (!itemToDelete.value) return
    try {
        const result = await poderiStore.deleteGeoElement(itemToDelete.value.id_geo_element)
        if (result.ok) {
            showDeleteGeoElementConfirm.value = false
            itemToDelete.value = null
            await loadGeoElements()
        } else {
            showDeleteGeoElementConfirm.value = false
            alert(result.message)
        }
    } catch (error) {
        console.error('Error deleting geo element:', error)
        alert(t('farmDetail.errorDeleteGeoElement'))
    }
}



async function deleteProduct() {
    if (!productToDelete.value) return
    try {
        const result = await poderiStore.deleteProduct(productToDelete.value.id_product)
        if (result.ok) {
            showDeleteProductConfirm.value = false
            productToDelete.value = null
            await poderiStore.fetchProductsByFarmCenter(farmCenterId)
        } else {
            showDeleteProductConfirm.value = false
            alert(result.message)
        }
    } catch (error) {
        console.error('Error deleting product:', error)
        alert(t('farmDetail.errorDeleteProduct'))
    }
}

async function handleProductSubmit() {
    showProductForm.value = false
    editingProductId.value = null
    productFormType.value = null
    await poderiStore.fetchProductsByFarmCenter(farmCenterId)
}

async function doDeleteFarmCenter() {
    deletingFarmCenter.value = true
    try {
        const result = await poderiStore.deleteFarmCenter(farmCenterId)
        if (result.ok) {
            showDeleteFarmCenterConfirm.value = false
            router.push({ name: 'FarmCenterListView' })
        } else {
            showDeleteFarmCenterConfirm.value = false
            alert(result.message)
        }
    } catch (e) {
        alert(t('farmDetail.errorDeleteFarm'))
    } finally {
        deletingFarmCenter.value = false
    }
}

// Season actions
function openSeasonForm(seasonId) {
    editingSeasonId.value = seasonId
    showSeasonForm.value = true
}

function editSeason(season) {
    openSeasonForm(season.id_season)
}

function confirmDeleteSeason(season) {
    itemToDelete.value = season
    showDeleteSeasonConfirm.value = true
}

async function deleteSeason() {
    if (!itemToDelete.value) return
    try {
        const result = await poderiStore.deleteSeason(itemToDelete.value.id_season)
        if (result.ok) {
            showDeleteSeasonConfirm.value = false
            itemToDelete.value = null
            await loadData()
        } else {
            showDeleteSeasonConfirm.value = false
            alert(result.message)
        }
    } catch (error) {
        console.error('Error deleting season:', error)
        alert(t('farmDetail.errorDeleteSeason'))
    }
}

async function handleSeasonSubmit() {
    showSeasonForm.value = false
    editingSeasonId.value = null
    await loadData()
}

// Dropdown "Aggiungi"
const addActions = computed(() => [
  { label: t('farmDetail.createPlot'),               value: 'plot' },
  { label: t('farmDetail.importPlotsAction'),        value: 'import-plots' },
  { label: t('farmDetail.createObservationAction'),  value: 'observation' },
  { label: t('farmDetail.createOperationAction'),    value: 'operation' },
])

function handleAddAction(item) {
  if (item.value === 'plot')              openPlotForm(null)
  else if (item.value === 'import-plots') router.push({ name: 'AddPlotsView', params: { farmCenterId } })
  else if (item.value === 'observation')  openObservationForm(null)
  else if (item.value === 'operation')    openOperationForm(null)
}

// Operation actions
function openOperationForm(operation) {
    editingOperation.value = operation
    showOperationForm.value = true
}

function editOperation(operation) {
    openOperationForm(operation)
}

function confirmDeleteOperation(operation) {
    operationToDelete.value = operation
    showDeleteOperationConfirm.value = true
}

async function deleteOperation() {
    if (!operationToDelete.value) return
    try {
        await dbStore.delete('operation', { id_operation: operationToDelete.value.id_operation })
        showDeleteOperationConfirm.value = false
        operationToDelete.value = null
        await poderiStore.fetchOperationsAndObservations(farmCenterId)
    } catch (error) {
        console.error('Error deleting operation:', error)
        alert(t('farmDetail.errorDeleteOperation'))
    }
}

async function handleOperationSaved() {
    console.log('handleOperationSaved called')
    showOperationForm.value = false
    editingOperation.value = null
    await poderiStore.fetchOperationsAndObservations(farmCenterId)
    console.log('Operations reloaded:', poderiStore.operations)
}

// Observation actions
function openObservationForm(observation) {
    editingObservation.value = observation
    showObservationForm.value = true
}

function editObservation(observation) {
    openObservationForm(observation)
}

function confirmDeleteObservation(observation) {
    observationToDelete.value = observation
    showDeleteObservationConfirm.value = true
}

async function deleteObservation() {
    if (!observationToDelete.value) return
    try {
        await dbStore.delete('observation', { id_observation: observationToDelete.value.id_observation })
        showDeleteObservationConfirm.value = false
        observationToDelete.value = null
        await poderiStore.fetchOperationsAndObservations(farmCenterId)
    } catch (error) {
        console.error('Error deleting observation:', error)
        alert(t('farmDetail.errorDeleteObservation'))
    }
}

async function handleObservationSaved() {
    showObservationForm.value = false
    editingObservation.value = null
    await poderiStore.fetchOperationsAndObservations(farmCenterId)
}

// Helper function for custom data display
function formatCustomData(item) {


 

    // Se non c'è schema, non mostrare nulla
    if (!item._schema) {
        return '-'
    }


    //se _schema è un oggetto trasformnalo in array mettendo la chiave in name
  if (!Array.isArray(item._schema)) {
    item._schema = Object.entries(item._schema).map(([key, value]) => ({
        name: key,
        ...value
    }))
  }

    
    // Estrai i nomi dei campi definiti nello schema
    const schemaFieldNames = item._schema.map(field => field.name)

    // Filtra solo i campi che sono nello schema
    const schemaEntries = Object.entries(item).filter(([key]) => schemaFieldNames.includes(key))

    if (schemaEntries.length === 0) return '-'
    return schemaEntries.map(([key, value]) => `${key}: ${value}`).join(', ')
}

// Watch selectedSeasonId to reload plots with fields
watch(selectedSeasonId, async (newSeasonId) => {
    if (newSeasonId && farmCenterId) {
        plotsWithFields.value = await poderiStore.getPlotFields(farmCenterId, newSeasonId)
    }
})

watch(farmCenter, (fc) => {
  if (fc && setHeader) {
    setHeader({ title: fc.name, subtitle: fc.place || '', breadcrumbs: props.breadcrumbs })
  }
}, { immediate: true })

onMounted(() => {
    loadData()
    poderiStore.fetchOperationsAndObservations(farmCenterId)
    actionsReady.value = true
})

onUnmounted(() => {
    clearHeader?.()
})

</script>

<style scoped>
.farm-center-detail {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.loading-state, .error-state {
  text-align: center;
  padding: 4rem 0;
}

.spinner {
  width: 2rem;
  height: 2rem;
  border: 2px solid #eee;
  border-top-color: var(--primary-color, #4299e1);
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
  margin: 0 auto 1rem;
}

@keyframes spin { to { transform: rotate(360deg); } }

.detail-wrapper {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.detail-content {
  display: flex;
  flex-direction: column;
  overflow: hidden;
  padding: 1rem;
}

.no-season-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  gap: 1rem;
  padding: 2rem;
  text-align: center;
  color: var(--text-secondary, #666);
}

.no-season-icon {
  color: var(--text-secondary, #aaa);
}

.no-season-state h3 {
  margin: 0;
  font-size: 1.25rem;
  color: var(--text-primary, #333);
}

.no-season-state p {
  margin: 0;
  font-size: 0.9rem;
}

.split-layout {
  display: flex;
  height: 100%;
  gap: 1rem;
}

.map-panel {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  position: relative;
}

.map-legend {
  position: absolute;
  bottom: 1rem;
  left: 1rem;
  background: white;
  padding: 1rem;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  max-width: 200px;
  max-height: 220px;
  display: flex;
  flex-direction: column;
}

@media (max-width: 600px) {
  .map-legend {
    padding: 0.5rem 0.65rem;
    max-width: 140px;
    max-height: 160px;
    border-radius: 6px;
  }
  .legend-title {
    font-size: 0.75rem;
    margin-bottom: 0.4rem;
  }
  .legend-item {
    font-size: 0.7rem;
    gap: 0.35rem;
  }
  .legend-color {
    width: 14px;
    height: 14px;
  }
  .legend-items {
    gap: 0.3rem;
  }
}

.legend-title {
  margin: 0 0 0.75rem;
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--text-primary, #1a202c);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  user-select: none;
}

.legend-title:hover {
  color: var(--primary-color, #4299e1);
}

.legend-items {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  overflow-y: auto;
}

.legend-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.813rem;
}

.legend-color {
  width: 20px;
  height: 20px;
  border-radius: 4px;
  border: 1px solid rgba(0, 0, 0, 0.1);
  flex-shrink: 0;
}

.legend-items--separate {
  margin-top: 6px;
  padding-top: 6px;
  border-top: 1px solid rgba(0, 0, 0, 0.08);
}

.legend-dot {
  width: 14px;
  height: 14px;
  border-radius: 50%;
  border: 1px solid rgba(0, 0, 0, 0.1);
  flex-shrink: 0;
}

.legend-label {
  color: var(--text-secondary, #4a5568);
}

.panel-1-layout,
.panel-layout {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
}

.info-panel {
  flex: 1;
  min-width: 0;
  min-height: 0;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 2rem;
  padding: 0 1rem 2rem 0;
}

/* Rimosso: .info-section (sostituito da .mb-5 utility) */

.section-title {
  font-size: 1.25rem;
  font-weight: 600;
  margin: 0;
  color: var(--text-primary, #1a202c);
}

.search-toolbar {
  display: flex;
  gap: 0.5rem;
  align-items: center;
  margin-bottom: 1rem;
}

.search-input {
  flex: 1;
}

.view-toggle {
  display: flex;
  gap: 0.15rem;
  flex-shrink: 0;
}

.data-table {
  overflow-x: auto;
}

.data-table :deep(.ae-table td:first-child),
.data-table :deep(.ae-table th:first-child) {
  padding: 4px 6px;
  width: 1%;
  white-space: nowrap;
}

.info-content p {
  margin: 0.5rem 0;
  font-size: 0.95rem;
}

@media (max-width: 1024px) {
  .split-layout {
    flex-direction: column;
  }
  
  .map-panel {
    height: 400px;
    flex: none;
  }
  
  .info-panel {
    flex: 1;
    padding: 0 0.5rem 2rem;
  }
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
}

.section-header h3 {
  font-size: 1.2rem;
  font-weight: 600;
  margin: 0;
}

.data-list {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 1rem;
}

.data-item-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.geo-element-detail-link {
  font-size: 0.8rem;
  color: var(--primary-color, #667eea);
  text-decoration: none;
  white-space: nowrap;
}
.geo-element-detail-link:hover {
  text-decoration: underline;
}

.empty-data p {
  margin: 0.5rem 0;
  color: #666;
}

.empty-choice-grid {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  max-width: 480px;
}

.empty-choice-card {
  cursor: pointer;
}

.empty-choice-body {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 0.5rem;
  padding: 1rem 0.5rem;
}

.empty-choice-icon {
  color: var(--primary-color, #4299e1);
  opacity: 0.8;
}

.empty-choice-title {
  font-weight: 600;
  font-size: 1rem;
  margin: 0 !important;
  color: var(--ae-text-primary, #1a202c);
}

.empty-choice-desc {
  font-size: 0.82rem;
  color: var(--ae-text-secondary, #6b7280);
  margin: 0 !important;
  line-height: 1.4;
}

.empty-data .hint {
  font-size: 0.875rem;
  color: #999;
  margin: 0 auto 1rem;
}

.data-section {
  margin-top: 2rem;
}

.data-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: 0.75rem;
}

/* Nel panel-3 stretto, riduci la larghezza minima delle card */
.panel-layout .data-grid {
  grid-template-columns: 1fr;
  gap: 0.5rem;
}

/* Limita larghezza card nel panel-3 */
.panel-layout .data-item {
  min-width: 0;
  overflow: hidden;
}

/* Empty plot card (dashed border) */
:deep(.field-card--empty) {
  border-style: dashed;
  border-color: #cbd5e0;
}

:deep(.plot-card--hovered) {
  outline: 2px solid var(--primary-color, #4299e1);
  outline-offset: 1px;
  box-shadow: 0 0 0 3px rgba(66, 153, 225, 0.2);
}

/* Crop selector modal */
.crop-selector-content {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.crop-selector-plot {
  font-size: 0.875rem;
  margin: 0;
}

.item-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.item-actions {
  display: flex;
  gap: 0.5rem;
}

.modal-actions {
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
  margin-top: 1rem;
}

.confirmation-text {
  color: var(--danger-color, #e53e3e);
  margin: 1rem 0;
  font-weight: 600;
}

.helper-text {
  font-size: 0.875rem;
  color: #999;
}

.detail-content {
  display: flex;
  flex-direction: column;
  gap: 2.5rem;
}

.tab-content {
  padding: 1.5rem 0;
}

.data-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.9rem;
}

.data-table thead {
  background: var(--table-header-bg, rgba(0, 0, 0, 0.02));
  border-bottom: 2px solid var(--border-color, #ddd);
}

.data-table th {
  padding: 1rem;
  text-align: left;
  font-weight: 600;
  color: var(--text-color, #333);
  white-space: nowrap;
}

.data-table td {
  padding: 0.875rem 1rem;
  border-bottom: 1px solid var(--table-border, rgba(0, 0, 0, 0.05));
}

.data-table tbody tr:last-child td {
  border-bottom: none;
}

.data-table tbody tr:hover {
  background: var(--table-row-hover, rgba(0, 0, 0, 0.02));
}

.data-table .actions-column {
  width: 100px;
  text-align: center;
  white-space: nowrap;
}

.custom-data {
  max-width: 300px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 0.85rem;
  color: #666;
}

/* Season Selector for Gantt */
.season-selector-hint {
  padding: 2rem;
  text-align: center;
  background: #f9f9f9;
  border-radius: 8px;
  margin-top: 1rem;
}

.season-selector-hint p {
  margin-bottom: 1rem;
  color: #666;
}

.season-buttons {
  display: flex;
  gap: 0.75rem;
  justify-content: center;
  flex-wrap: wrap;
}

.season-btn {
  min-width: 120px;
}

.season-selector-compact {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem;
  background: #f9f9f9;
  border-radius: 8px;
  margin-bottom: 1rem;
}

.season-selector-compact label {
  font-weight: 600;
  color: #333;
}

.season-select {
  padding: 0.6rem 1rem;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 0.95rem;
  font-family: inherit;
  background: white;
  cursor: pointer;
  transition: border-color 0.2s;
}

.season-select:focus {
  outline: none;
  border-color: #667eea;
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
}

@media (max-width: 768px) {
  .data-table {
    font-size: 0.8rem;
  }

  .data-table th,
  .data-table td {
    padding: 0.75rem 0.5rem;
  }

  .custom-data {
    max-width: 150px;
  }

  .season-buttons {
    flex-direction: column;
  }

  .season-btn {
    width: 100%;
  }
}

.zoo-prompt p {
  margin: 0 0 1rem;
  color: var(--text-secondary, #666);
}

.zoo-prompt-actions {
  display: flex;
  gap: 0.75rem;
  flex-wrap: wrap;
}

/* Farm type choice screen */
.farm-type-choice {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 1.5rem 1rem;
  gap: 0.75rem;
  max-width: 640px;
  margin: 0 auto;
}

@media (max-width: 640px) {
  .farm-type-choice {
    padding: 0.75rem 0.5rem;
    gap: 0.5rem;
  }
}

.farm-type-icon {
  color: var(--text-secondary, #718096);
}

.farm-type-choice h3 {
  margin: 0;
  font-size: 1.3rem;
  font-weight: 700;
}

.farm-type-options {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  width: 100%;
  margin-top: 0.5rem;
}

.farm-type-card:last-child {
  grid-column: 1 / -1;
}

@media (max-width: 640px) {
  .farm-type-options {
    grid-template-columns: 1fr;
  }
  .farm-type-card:last-child {
    grid-column: auto;
  }
  .farm-type-card {
    padding: 0.6rem 0.75rem;
    gap: 0.6rem;
  }
  .farm-type-card span {
    display: none;
  }
}

.farm-type-card {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem;
  cursor: pointer;
  text-align: left;
}

.farm-type-card-icon {
  flex-shrink: 0;
  color: var(--primary-color, #4299e1);
}

.farm-type-card-text {
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
}

.farm-type-card strong {
  font-size: 0.9rem;
  font-weight: 700;
}

.farm-type-card span {
  font-size: 0.78rem;
  color: var(--text-secondary, #718096);
  line-height: 1.4;
}

/* Zoo-only wrapper */
.zoo-only-wrapper {
  padding: 1rem;
}

.summary-card {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 1rem 1.25rem 0.75rem;
  cursor: pointer;
  min-height: 90px;
}

.summary-card-label {
  font-size: 0.78rem;
  font-weight: 600;
  color: var(--text-secondary, #718096);
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin-bottom: 0.35rem;
}

/* Nel panel-3, layout 2 colonne per Registro e font più piccolo */
.panel-layout .info-section:first-of-type .data-grid {
  grid-template-columns: repeat(2, 1fr);
}

.panel-layout .summary-card {
  min-height: 75px;
  padding: 0.75rem 0.75rem 0.5rem;
}

.panel-layout .summary-card:last-child {
  grid-column: 1 / -1;
}

.panel-layout .summary-card-label {
  font-size: 0.65rem;
  margin-bottom: 0.25rem;
}

.panel-layout .summary-card-count {
  font-size: 1.8rem;
}

.summary-card-count {
  font-size: 2.2rem;
  font-weight: 700;
  line-height: 1;
  color: var(--text-primary, #2d3748);
}

.summary-card-arrow {
  position: absolute;
  bottom: 0.5rem;
  right: 0.5rem;
  color: var(--text-secondary, #aaa);
}

.voice-fab {
  position: fixed;
  bottom: 4rem;
  right: 1.75rem;
  width: 3.25rem;
  height: 3.25rem;
  border-radius: 50%;
  border: none;
  background: var(--color-primary, #4f7942);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 14px rgba(0,0,0,0.22);
  cursor: pointer;
  z-index: 800;
  transition: transform 0.15s, box-shadow 0.15s;
}

.voice-fab:hover {
  transform: scale(1.07);
  box-shadow: 0 6px 18px rgba(0,0,0,0.28);
}
</style>
